import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Star, Trophy, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../AuthContext';
import LoveGardenGame from '../components/LoveGardenGame';

// Use relative path for API calls - backend is on same domain
const API_BASE_URL = '';

// Define worlds OUTSIDE component to avoid re-creation on every render
const WORLDS = [
  { start: 1, end: 25, name: "Heart's Awakening", emoji: "💗", id: 1 },
  { start: 26, end: 50, name: "Inner Peace", emoji: "🌸", id: 2 },
  { start: 51, end: 75, name: "Starlit Dreams", emoji: "⭐", id: 3 },
  { start: 76, end: 100, name: "Crystal Clarity", emoji: "💎", id: 4 },
  { start: 101, end: 125, name: "Transformation Garden", emoji: "🦋", id: 5 },
  { start: 126, end: 150, name: "Eternal Bloom", emoji: "🌹", id: 6 }
];

const LoveGardenSimple = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingLevel, setPlayingLevel] = useState(null);
  const [selectedWorld, setSelectedWorld] = useState(null); // Track selected world tab

  // Calculate values from progress (must be before useEffect)
  const currentLevel = progress?.current_level || 1;
  const highestUnlocked = progress?.highest_level_unlocked || 1;

  // First useEffect: Load progress on mount
  useEffect(() => {
    if (token) {
      loadProgress();
    } else {
      setError('Please log in to access Love Garden');
      setLoading(false);
    }
  }, [token]);

  // Second useEffect: Auto-select current world (MUST be before conditional returns!)
  useEffect(() => {
    if (!selectedWorld && progress) {
      const currentWorld = WORLDS.find(w => currentLevel >= w.start && currentLevel <= w.end);
      setSelectedWorld(currentWorld || WORLDS[0]);
    }
  }, [progress, selectedWorld, currentLevel]);

  const loadProgress = async () => {
    try {
      setLoading(true);
      
      console.log('Fetching Love Garden for user:', user?.email);
      console.log('Token exists:', !!token);
      
      const response = await fetch(`${API_BASE_URL}/api/love-garden/status`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Love Garden response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Love Garden data loaded:', data);
        setProgress(data);
        setError(null);
      } else {
        const errorData = await response.text();
        console.error('Love Garden error response:', response.status, errorData);
        
        if (response.status === 401 || response.status === 403) {
          setError('Session expired. Please log out and log in again');
        } else {
          setError(`Failed to load game (Error ${response.status})`);
        }
      }
    } catch (err) {
      console.error('Error loading Love Garden:', err);
      setError('Failed to connect. Please check your internet connection');
    } finally {
      setLoading(false);
    }
  };

  const getWorldInfo = (level) => {
    if (level <= 25) return { name: "Heart's Awakening", emoji: "💗", color: "rose" };
    if (level <= 50) return { name: "Inner Peace", emoji: "🌸", color: "blue" };
    if (level <= 75) return { name: "Starlit Dreams", emoji: "✨", color: "purple" };
    if (level <= 100) return { name: "Crystal Clarity", emoji: "💎", color: "cyan" };
    if (level <= 125) return { name: "Transformation Garden", emoji: "🦋", color: "amber" };
    return { name: "Eternal Bloom", emoji: "🌹", color: "pink" };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center">
          <div className="text-6xl mb-4">🌸</div>
          <p className="text-gray-600 font-sans">Loading your garden...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <div className="text-6xl mb-4">🌱</div>
          <h2 className="text-2xl font-serif font-bold mb-4">Welcome to Love Garden!</h2>
          <p className="text-gray-600 mb-6 font-sans">{error}</p>
          <Button 
            onClick={loadProgress} 
            className="bg-rose-500 hover:bg-rose-600 text-white font-sans"
          >
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  // Handler functions
  const handleLevelComplete = (result) => {
    loadProgress();
    setPlayingLevel(null);
  };

  // Conditional renders (AFTER all hooks)
  if (playingLevel) {
    return (
      <LoveGardenGame
        levelNumber={playingLevel}
        onClose={() => setPlayingLevel(null)}
        onComplete={handleLevelComplete}
        token={token}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 pb-24" style={{paddingTop: '64px'}}>
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center space-x-2 mb-6">
          <button
            onClick={() => window.history.back()}
            className="p-1.5 hover:bg-white/80 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-serif font-bold text-gray-900">Love Garden</h1>
            <p className="text-gray-600 text-xs">Level {currentLevel} • {progress?.total_xp || 0} XP</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-4 text-center bg-white/80">
            <div className="text-2xl font-bold text-rose-500">{progress?.streak_days || 0}</div>
            <div className="text-xs text-gray-600 font-sans">Day Streak 🔥</div>
          </Card>
          <Card className="p-4 text-center bg-white/80">
            <div className="text-2xl font-bold text-purple-500">{progress?.total_levels_completed || 0}</div>
            <div className="text-xs text-gray-600 font-sans">Completed ✓</div>
          </Card>
          <Card className="p-4 text-center bg-white/80">
            <div className="text-2xl font-bold text-pink-500">
              {progress?.unlocked_badges ? progress.unlocked_badges.length : 0}
            </div>
            <div className="text-xs text-gray-600 font-sans">Badges 🏆</div>
          </Card>
        </div>

        {/* Worlds Navigation - Tabs */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          {WORLDS.map((world) => {
            const isUnlocked = highestUnlocked >= world.start;
            const isSelected = selectedWorld?.id === world.id;
            
            return (
              <Card 
                key={world.id}
                className={`p-4 text-center cursor-pointer transition-all ${
                  isSelected
                    ? 'ring-2 ring-rose-500 bg-gradient-to-br from-rose-50 to-pink-50 scale-105' 
                    : isUnlocked 
                    ? 'bg-white hover:bg-gray-50 hover:scale-105' 
                    : 'bg-gray-100 opacity-60 cursor-not-allowed'
                }`}
                onClick={() => isUnlocked && setSelectedWorld(world)}
              >
                <div className="text-4xl mb-2">{world.emoji}</div>
                <div className="text-xs font-medium text-gray-900 font-sans">
                  {world.name}
                </div>
                {!isUnlocked && <Lock className="w-4 h-4 mx-auto mt-2 text-gray-400" />}
              </Card>
            );
          })}
        </div>

        {/* Level Map - Selected World Only */}
        {selectedWorld && (
          <Card className="p-6 bg-white/90">
            {/* World Header */}
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="text-4xl">{selectedWorld.emoji}</span>
              <span>{selectedWorld.name}</span>
              <span className="text-sm text-gray-500 font-normal">
                (Levels {selectedWorld.start}-{selectedWorld.end})
              </span>
            </h2>
            
            {/* Level Grid for Selected World */}
            <div className="grid grid-cols-5 md:grid-cols-10 gap-3 mb-6">
              {Array.from({ length: selectedWorld.end - selectedWorld.start + 1 }, (_, i) => selectedWorld.start + i).map(level => {
                    const isUnlocked = level <= highestUnlocked;
                    const isCurrent = level === currentLevel;
                    const isCompleted = level < currentLevel;
                    const worldInfo = getWorldInfo(level);
                    
                    return (
                      <button
                        key={level}
                        disabled={!isUnlocked}
                        className={`relative h-20 rounded-xl font-serif font-bold text-lg transition-all ${
                          isCurrent
                            ? 'bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-lg scale-110 ring-4 ring-rose-300'
                            : isCompleted
                            ? 'bg-gradient-to-br from-green-100 to-green-200 text-green-700 hover:scale-105 shadow-md'
                            : isUnlocked
                            ? 'bg-white text-gray-900 border-2 border-rose-200 hover:scale-105 hover:border-rose-400 shadow'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                        onClick={() => isUnlocked && setPlayingLevel(level)}
                      >
                        {/* Level number */}
                        <div>{level}</div>
                        
                        {/* Status indicator */}
                        {isCompleted && (
                          <div className="absolute top-1 right-1">
                            <Star className="w-4 h-4 text-green-600 fill-green-600" />
                          </div>
                        )}
                        {!isUnlocked && (
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <Lock className="w-6 h-6" />
                          </div>
                        )}
                        {isCurrent && (
                          <div className="absolute -top-2 -right-2 bg-yellow-400 rounded-full p-1">
                            <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                          </div>
                        )}
                      </button>
                    );
                  })}
            </div>

            {/* Legend */}
          <div className="flex flex-wrap justify-center gap-6 text-sm border-t pt-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-600 rounded-lg"></div>
              <span className="text-gray-700 font-sans">Current Level</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-green-100 to-green-200 rounded-lg border border-green-300"></div>
              <span className="text-gray-700 font-sans">Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gray-200 rounded-lg opacity-60"></div>
              <span className="text-gray-700 font-sans">Locked</span>
            </div>
          </div>
        </Card>
        )}
      </div>
    </div>
  );
};

export default LoveGardenSimple;
